/**
 * ISO 27001 · Change Management tab
 *
 * Full CRUD register for configuration/permission/integration/infra changes.
 * Enforces:
 *   - Lifecycle: requested → approved → in_progress → deployed → verified
 *                                                          ↓
 *                                                  rolled_back | rejected
 *   - Rollback plan + verification steps are MANDATORY before "deployed"
 *     for medium/high risk changes (server enforces too — we mirror the
 *     validation on the client for a friendlier UX).
 *   - Every transition writes a history entry with the acting user + remark.
 *
 * Reused for both tenant-scope (`/iso27001/changes`) and platform-scope
 * (`/iso27001/changes/platform`) — pass `scope="platform"` from the admin
 * page. This component makes no assumption about who's logged in beyond
 * the API path.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/time";
import { Plus, GitBranch, ShieldAlert, CheckCircle2, RotateCcw, XCircle, ClipboardList } from "lucide-react";

const STATUSES = ["requested", "approved", "in_progress", "deployed", "verified", "rolled_back", "rejected"];
const RISK_LEVELS = ["low", "medium", "high"];
const CATEGORIES = ["config", "permissions", "integration", "infra", "data", "access", "other"];

const STATUS_TONE = {
    requested:   "bg-blue-500/15 text-blue-400 border-blue-500/40",
    approved:    "bg-indigo-500/15 text-indigo-400 border-indigo-500/40",
    in_progress: "bg-amber-500/15 text-amber-400 border-amber-500/40",
    deployed:    "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
    verified:    "bg-emerald-500/25 text-emerald-300 border-emerald-500/60",
    rolled_back: "bg-orange-500/15 text-orange-400 border-orange-500/40",
    rejected:    "bg-rose-500/15 text-rose-400 border-rose-500/40",
};

const RISK_TONE = {
    low:    "bg-slate-500/15 text-slate-400",
    medium: "bg-amber-500/15 text-amber-400",
    high:   "bg-rose-500/15 text-rose-400",
};

/**
 * @param {{scope?: "tenant" | "platform"}} props
 */
export default function ChangeManagementTab({ scope = "tenant" }) {
    const basePath = scope === "platform" ? "/iso27001/changes/platform" : "/iso27001/changes";
    const [rows, setRows] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [statusFilter, setStatusFilter] = useState("all");
    const [form, setForm] = useState({
        title: "", description: "", category: "config", risk_level: "low",
        rollback_plan: "", verification_steps: "",
    });

    const load = async () => {
        try {
            const { data } = await api.get(basePath);
            setRows(data);
        } catch (e) { toast.error(apiError(e)); }
    };
    // basePath is a stable string derived from a stable prop -> safe to omit
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [scope]);

    const create = async () => {
        if (!form.title.trim() || form.title.trim().length < 3) {
            toast.error("Title must be at least 3 characters"); return;
        }
        if (!form.description.trim() || form.description.trim().length < 5) {
            toast.error("Description must be at least 5 characters"); return;
        }
        if (["medium", "high"].includes(form.risk_level) && !form.rollback_plan.trim()) {
            toast.error("Rollback plan is required for medium/high risk changes"); return;
        }
        try {
            await api.post(basePath, form);
            toast.success("Change logged");
            setForm({ title: "", description: "", category: "config", risk_level: "low",
                       rollback_plan: "", verification_steps: "" });
            setShowForm(false);
            load();
        } catch (e) { toast.error(apiError(e)); }
    };

    const transition = async (row, newStatus) => {
        const remark = window.prompt(
            `Move "${row.title}" to ${newStatus}.\nAdd a remark for the audit trail (optional):`, "");
        if (remark === null) return; // user cancelled
        // Client-side gate — server enforces this too but we surface the error early
        if (newStatus === "deployed") {
            const rp = row.rollback_plan || "";
            const vs = row.verification_steps || "";
            if (!rp.trim() || !vs.trim()) {
                toast.error("Add a rollback plan AND verification steps before marking deployed"); return;
            }
        }
        try {
            await api.patch(`${basePath}/${row.id}`, { status: newStatus, remark: remark || undefined });
            toast.success(`Status → ${newStatus.replace("_", " ")}`);
            load();
        } catch (e) { toast.error(apiError(e)); }
    };

    const filtered = statusFilter === "all" ? rows : rows.filter(r => r.status === statusFilter);

    return (
        <div className="space-y-4" data-testid="iso-changes-tab">
            <div className="flex justify-between items-start gap-3 flex-wrap">
                <div>
                    <div className="overline">Change Management · ISO 27001 A.8.32</div>
                    <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                        Log every change to configs, permissions, integrations or infra.
                        Medium/high risk changes require a rollback plan. Deployment requires
                        verification steps. Full audit trail on every state change.
                    </p>
                </div>
                <button onClick={() => setShowForm(v => !v)} data-testid="iso-change-toggle-create"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:opacity-90 text-xs uppercase tracking-[0.15em]">
                    <Plus className="w-3 h-3"/> Log change
                </button>
            </div>

            {showForm && (
                <div className="bg-card border border-primary/40 p-6 space-y-3" data-testid="iso-change-form">
                    <div className="overline text-primary">New change request</div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <label className="text-xs space-y-1 block">
                            <span className="text-muted-foreground">Title *</span>
                            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                                className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm"
                                data-testid="iso-change-title"/>
                        </label>
                        <label className="text-xs space-y-1 block">
                            <span className="text-muted-foreground">Category</span>
                            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                                className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm"
                                data-testid="iso-change-category">
                                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </label>
                        <label className="text-xs space-y-1 block">
                            <span className="text-muted-foreground">Risk level</span>
                            <select value={form.risk_level} onChange={e => setForm({ ...form, risk_level: e.target.value })}
                                className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm"
                                data-testid="iso-change-risk">
                                {RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </label>
                    </div>
                    <label className="text-xs space-y-1 block">
                        <span className="text-muted-foreground">Description *</span>
                        <textarea rows={3} value={form.description}
                            onChange={e => setForm({ ...form, description: e.target.value })}
                            className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm"
                            data-testid="iso-change-description"/>
                    </label>
                    <label className="text-xs space-y-1 block">
                        <span className="text-muted-foreground">
                            Rollback plan {["medium","high"].includes(form.risk_level) && <span className="text-rose-400">(required)</span>}
                        </span>
                        <textarea rows={2} value={form.rollback_plan}
                            onChange={e => setForm({ ...form, rollback_plan: e.target.value })}
                            className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm"
                            data-testid="iso-change-rollback"/>
                    </label>
                    <label className="text-xs space-y-1 block">
                        <span className="text-muted-foreground">Verification steps <span className="text-muted-foreground/70">(required before deployment)</span></span>
                        <textarea rows={2} value={form.verification_steps}
                            onChange={e => setForm({ ...form, verification_steps: e.target.value })}
                            className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm"
                            data-testid="iso-change-verification"/>
                    </label>
                    <div className="flex justify-end gap-2">
                        <button onClick={() => setShowForm(false)}
                            className="px-4 py-2 text-xs text-muted-foreground hover:text-foreground">Cancel</button>
                        <button onClick={create} data-testid="iso-change-submit"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:opacity-90 text-xs uppercase tracking-[0.15em]">
                            Submit for approval
                        </button>
                    </div>
                </div>
            )}

            {/* Filter chips */}
            <div className="flex gap-1 overflow-x-auto pb-1">
                {["all", ...STATUSES].map(s => (
                    <button key={s} onClick={() => setStatusFilter(s)}
                        className={`text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 border whitespace-nowrap transition-colors ${statusFilter === s ? "border-primary text-primary" : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"}`}
                        data-testid={`iso-change-filter-${s}`}>
                        {s.replace("_", " ")}
                    </button>
                ))}
            </div>

            {filtered.length === 0 ? (
                <div className="bg-card border border-border p-10 text-center text-xs text-muted-foreground">
                    <GitBranch className="w-8 h-8 mx-auto mb-3 opacity-30"/>
                    No change records {statusFilter !== "all" && `in "${statusFilter}"`} yet.
                </div>
            ) : (
                <div className="space-y-2">
                    {filtered.map(row => <ChangeRow key={row.id} row={row} onTransition={transition}/>)}
                </div>
            )}
        </div>
    );
}

function ChangeRow({ row, onTransition }) {
    const [open, setOpen] = useState(false);
    const nextActions = nextTransitions(row.status);
    return (
        <div className="bg-card border border-border p-4 hover:border-primary/40 transition-colors" data-testid={`iso-change-row-${row.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={() => setOpen(v => !v)} className="text-sm text-foreground hover:text-primary text-left">
                            {row.title}
                        </button>
                        <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border ${STATUS_TONE[row.status]}`}>
                            {row.status.replace("_", " ")}
                        </span>
                        <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 ${RISK_TONE[row.risk_level]}`}>
                            {row.risk_level} risk
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">· {row.category}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{row.description}</div>
                    <div className="text-[10px] text-muted-foreground mt-1.5">
                        Requested {fmtDateTime(row.requested_at)} by {row.requested_by_email || "—"}
                        {row.approved_at && <> · Approved {fmtDateTime(row.approved_at)}</>}
                        {row.deployed_at && <> · Deployed {fmtDateTime(row.deployed_at)}</>}
                        {row.verified_at && <> · Verified {fmtDateTime(row.verified_at)}</>}
                    </div>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                    {nextActions.map(({ status, icon: Icon, label }) => (
                        <button key={status} onClick={() => onTransition(row, status)}
                            data-testid={`iso-change-${row.id}-${status}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 border border-border hover:border-primary hover:text-primary text-[10px] uppercase tracking-[0.15em]">
                            <Icon className="w-3 h-3"/> {label}
                        </button>
                    ))}
                </div>
            </div>
            {open && (
                <div className="mt-4 pt-4 border-t border-border grid sm:grid-cols-2 gap-4 text-xs">
                    <div>
                        <div className="overline text-muted-foreground mb-1">Rollback plan</div>
                        <div className="text-foreground whitespace-pre-wrap">{row.rollback_plan || <span className="text-muted-foreground/60">— none —</span>}</div>
                    </div>
                    <div>
                        <div className="overline text-muted-foreground mb-1">Verification steps</div>
                        <div className="text-foreground whitespace-pre-wrap">{row.verification_steps || <span className="text-muted-foreground/60">— none —</span>}</div>
                    </div>
                    {row.history?.length > 0 && (
                        <div className="sm:col-span-2">
                            <div className="overline text-muted-foreground mb-1 flex items-center gap-1"><ClipboardList className="w-3 h-3"/>History</div>
                            <div className="space-y-1.5 max-h-64 overflow-y-auto">
                                {row.history.map((h, i) => (
                                    <div key={i} className="flex items-start gap-2 text-[11px]">
                                        <div className="text-muted-foreground/70 shrink-0 w-32">{fmtDateTime(h.ts)}</div>
                                        <div className="flex-1 min-w-0">
                                            <span className="text-foreground">{h.actor_email || "system"}</span>
                                            <span className="text-muted-foreground"> · </span>
                                            <span className="text-primary">{(h.from_status || "—").replace("_", " ")} → {(h.to_status || "").replace("_", " ")}</span>
                                            {h.remark && <div className="text-muted-foreground mt-0.5">{h.remark}</div>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function nextTransitions(status) {
    // Legal transitions from current state (mirrors backend policy).
    switch (status) {
        case "requested":   return [
            { status: "approved",    icon: CheckCircle2, label: "Approve" },
            { status: "rejected",    icon: XCircle,      label: "Reject" },
        ];
        case "approved":    return [
            { status: "in_progress", icon: ShieldAlert,  label: "Start work" },
            { status: "rejected",    icon: XCircle,      label: "Reject" },
        ];
        case "in_progress": return [
            { status: "deployed",    icon: CheckCircle2, label: "Mark deployed" },
            { status: "rolled_back", icon: RotateCcw,    label: "Roll back" },
        ];
        case "deployed":    return [
            { status: "verified",    icon: CheckCircle2, label: "Verify" },
            { status: "rolled_back", icon: RotateCcw,    label: "Roll back" },
        ];
        default: return [];
    }
}
